import Swiper from 'swiper/bundle';
import PubSub from 'pubsub-js';
import { MEDIA_QUERIES, BREAK_POINTS } from '@/js/constants';
import { createUID } from '@/js/utils/faker';
import { windowState } from '@/js/WindowState';

/**
 * @doc https://idangero.us/swiper/api/
 * @doc https://idangero.us/swiper/demos/
 */
export default class Carousel {
  constructor (selector) {
    this.el = [...document.querySelectorAll(selector)];

    this.options = {
      billboard: {
        slidesPerView: 1,
        spaceBetween: 0,
        active: true,
        autoplay: {
          delay: 5000,
          disableOnInteraction: false
        },
        nav: {
          prevEl: '.swiper-button-prev',
          nextEl: '.swiper-button-next'
        },
        pagination: {
          el: '.swiper-pagination'
        },
        breakpoints: {
          // when window width is >= BREAK_POINT
          [MEDIA_QUERIES.MOBILE]: {
            slidesPerView: 1,
            spaceBetween: 20,
            active: true
          }
        }
      },
      post: {
        slidesPerView: 1,
        spaceBetween: 0,
        active: true,
        nav: {
          prevEl: '.swiper-button-prev',
          nextEl: '.swiper-button-next'
        },
        pagination: {
          el: '.swiper-pagination'
        },
        autoplay: {
          delay: 5000
        },
        breakpoints: {
          // when window width is >= BREAK_POINT
          [MEDIA_QUERIES.MOBILE]: {
            slidesPerView: 1,
            spaceBetween: 20,
            active: true
          }
        }
      },
      history: {
        slidesPerView: 2,
        spaceBetween: 24,
        active: true,
        nav: {
          prevEl: '.swiper-button-prev',
          nextEl: '.swiper-button-next'
        },
        pagination: {
          el: '.swiper-pagination'
        },
        breakpoints: {
          // when window width is >= BREAK_POINT
          [MEDIA_QUERIES.MOBILE]: {
            slidesPerView: 1,
            spaceBetween: 20,
            active: true
          }
        }
      },
      cards: {
        slidesPerView: 1,
        spaceBetween: 24,
        active: true,
        autoplay: {
          delay: 5000
        },
        nav: {
          prevEl: '.swiper-button-prev',
          nextEl: '.swiper-button-next'
        },
        pagination: {
          el: '.swiper-pagination'
        },
        breakpoints: {
          // when window width is >= BREAK_POINT
          [BREAK_POINTS.DESKTOP]: {
            slidesPerView: 3,
            active: true
          }
        }
      },
      notice: {
        slidesPerView: 1,
        spaceBetween: 0,
        active: true,
        autoplay: {
          delay: 5000,
          disableOnInteraction: false
        },
        nav: {
          prevEl: '.swiper-button-prev',
          nextEl: '.swiper-button-next'
        },
        pagination: {
          el: '.swiper-pagination'
        },
        breakpoints: {
          [MEDIA_QUERIES.MOBILE]: {
            slidesPerView: 1,
            active: true
          }
        }
      }
    };

    if (this.el.length) {
      this.initialize({
        refresh: false
      });
      this.bind();
    }
  }

  initialize ({ refresh }) {
    this.instances = (refresh ? this.instances : this.el)
      .map(data => {
        const el = refresh ? data.swiper.el : data;
        const options = this.createOptions(el);
        const now = this.getNowOptions(options);
        const uid = createUID();
        el.dataset.uid = uid; // DOM要素に正しい UID を設定

        if (refresh) {
          data.swiper.destroy();

          if (now.nav) {
            this.detachNav(data);
          }
        }

        // 現在のオプションが active: false ならインスタンス化しない
        if (!this.isActive(now)) {
          return false;
        }

        const swiper = new Swiper(el, options);
        const instance = { uid, swiper };

        // bulletsで移動させる場合
        // const bullets = [...el.querySelectorAll('.swiper-pagination-bullet')];

        // bullets.forEach((bullet, i) => {
        //   bullet.setAttribute('aria-label', 'スライド ' + (i + 1));
        //   bullet.setAttribute('role', 'button');
        //   bullet.setAttribute('tabindex', '0');

        //   bullet.addEventListener('keydown', e => {
        //     const target = e.target;
        //     if (e.key === 'Enter' || e.key === ' ') {
        //       e.preventDefault();
        //       target.click();
        //     }
        //   });
        // });

        if (now.nav) {
          this.attachNav(instance, now);
        }
        if (el.querySelectorAll('.swiper-slide').length <= swiper.params.slidesPerView) {
          this.hideControls(el);
        }

        return instance;
      })
      .filter(Boolean);
  }

  bind () {
    this.onChangeViewport = this.onChangeViewport.bind(this);
    PubSub.subscribe('App.changeViewport', this.onChangeViewport);

    // すべてのカルーセル要素にクリックイベントリスナーを追加、onClickToggleを実行
    this.el.forEach(carousel => {
      carousel.addEventListener('click', this.onClickToggle.bind(this));
    });
  }

  /**
   * @doc navigationのボタンを初期化 （デフォルト機能のボタンだと、複数カルーセル用にイベントを動的生成できない）
   */
  attachNav (instance, options) {
    const prevEl = instance.swiper.el.querySelector(options.nav.prevEl);
    const nextEl = instance.swiper.el.querySelector(options.nav.nextEl);
    // console.log('attachNav:', { prevEl, nextEl });

    const handlePrevSlide = () => {
      instance.swiper.slidePrev();
    };
    const handleNextSlide = () => {
      instance.swiper.slideNext();
    };

    if (prevEl) {
      prevEl.addEventListener('click', handlePrevSlide);
    }
    if (nextEl) {
      nextEl.addEventListener('click', handleNextSlide);
    }

    instance.prevEl = prevEl;
    instance.nextEl = nextEl;
    instance.handlePrevSlide = handlePrevSlide;
    instance.handleNextSlide = handleNextSlide;
  }

  /**
   * @doc navigationのボタンを解除
   */
  detachNav (instance) {
    if (instance.prevEl) {
      instance.prevEl.removeEventListener('click', instance.handlePrevSlide);
    }
    if (instance.nextEl) {
      instance.nextEl.removeEventListener('click', instance.handleNextSlide);
    }
  }

  /**
   * @doc [data-options=""] に指定された既定オプションと、デフォルトオプション をマージしてオプションを生成
   */
  createOptions (el) {
    const slideLength = el.querySelectorAll('.swiper-slide').length;
    const typeOptions = this.options[el.dataset.option];
    const options = {
      speed: 400,
      autoHeight: true,
      watchOverflow: true,
      loop: true,
      ...typeOptions
    };

    // スライド数が少ない場合、スライド化しないオプション
    // スライド数が多い場合、loop を trueにしていると、無効化されてしまうため
    if (options.watchOverflow) {
      const isEnoughSlide = this.isEnoughSlideForWatchOverflow(slideLength, options.slidesPerView);
      options.loop = isEnoughSlide;
    }
    if (options.breakpoints) {
      Object.entries(options.breakpoints).forEach((entry = []) => {
        const childOptions = entry[1] || {};
        const isEnoughSlide = this.isEnoughSlideForWatchOverflow(slideLength, childOptions.slidesPerView);
        childOptions.loop = isEnoughSlide;
      });
    }
    return options;
  }

  /**
   * @doc 現在のブレークポイントで使われるオプションを取得 （Swiperをインスタンス化せずに取得するため）
   */
  getNowOptions (options) {
    let nowOpts = {};

    if (!options.breakpoints || !Object.keys(options.breakpoints).length) {
      return options;
    }

    Object.keys(options.breakpoints).map(bp => {
      if (bp >= windowState.state.width) {
        nowOpts = { ...options.breakpoints[bp] };
      }
    });

    return Object.keys(nowOpts).length ? nowOpts : options;
  }

  /**
   * @doc 拡張オプション（active） の状態チェック
   */
  isActive (options) {
    return Object.prototype.hasOwnProperty.call(options, 'active') && options.active;
  }

  /**
   * @doc Swiper オプションに指定した表示数 （perView） を、実際に存在するスライド数が満たしているか
   */
  isEnoughSlideForWatchOverflow (slideLen, perView) {
    return slideLen > (perView === 'auto' ? 0 : Number(perView));
  }

  /**
   * @doc ブレークポイント変更時のイベント
   */
  onChangeViewport () {
    // インスタンスが存在しない場合は初期化
    if (!this.instances.length) {
      this.initialize({
        refresh: false
      });
      return;
    }

    // インスタンスが存在する場合は、リフレッシュ（オプション更新）
    this.initialize({
      refresh: true
    });
  }

  /**
   * @doc 自動再生の停止・開始
   */
  onClickToggle (e) {
    const target = e.target;

    // event target が再生・停止ボタンでない場合は処理しない
    if (!target.closest('.js-carousel-pause') && !target.closest('.js-carousel-play')) {
      return;
    }

    const instance = this.instances.find(instance => instance.uid === e.currentTarget.dataset.uid);
    const el = instance.swiper.el;
    const pauseButton = el.querySelector('.js-carousel-pause');
    const playButton = el.querySelector('.js-carousel-play');

    if (instance.swiper.autoplay.running) {
      // 停止する
      instance.swiper.autoplay.stop();
      pauseButton.dataset.hidden = true;
      playButton.dataset.hidden = false;
    } else {
      // 再生する
      instance.swiper.autoplay.start();
      pauseButton.dataset.hidden = false;
      playButton.dataset.hidden = true;
    }
  }

  /**
   * @doc コントロールを非表示にする
   */
  hideControls (el) {
    const controls = el.querySelector('.js-carousel-controls');
    controls.style.display = 'none';
  }
}
